import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  BarChart3, 
  FileText, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Download,
  Filter,
  AlertCircle,
  PieChart as PieChartIcon,
  X,
  ChevronsUpDown,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useSession } from "../App";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface ReportSummary {
  totalAudits: number;
  completedAudits: number;
  pendingAudits: number;
  averageScore: number;
  completionRate: number;
  homes: { id: number; name: string }[];
}

interface AuditReport {
  id: number;
  name: string;
  status: string;
  score: number | null;
  home_name: string;
  template_name: string;
  created_by_name: string;
  created_at: string;
  audit_type: string;
  type_id: number | null;
  category_id: number | null;
}

interface TrendData {
  week: string;
  total: number;
  completed: number;
  avgScore: number;
  completionRate: number;
}

interface AuditMetric {
  id: number;
  name: string;
  home_name: string;
  template_name: string;
  created_at: string;
  submitted_at: string | null;
  score: number;
  total_actions: number;
  completed_actions: number;
  action_completion_rate: number;
  completion_time_hours: number | null;
  avg_action_resolution_hours: number | null;
  created_by_name: string;
  good_practices_count: number;
}

const COLORS = ['hsl(var(--chart-2))', 'hsl(var(--chart-4))', 'hsl(var(--destructive))'];

export default function ReportsPage() {
  const { session, isLoading } = useSession();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedHomes, setSelectedHomes] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("all");
  const [selectedScoreType, setSelectedScoreType] = useState<'all'|'scored'|'unscored'>('all');
  const [scoreTypeSelectOpen, setScoreTypeSelectOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>("all");
  const [homeSelectOpen, setHomeSelectOpen] = useState(false);
  const [orgSelectOpen, setOrgSelectOpen] = useState(false);
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [categorySelectOpen, setCategorySelectOpen] = useState(false);
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  const [statusSelectOpen, setStatusSelectOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportContent, setExportContent] = useState<'charts' | 'table' | 'both'>('both');
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv'>('pdf');
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [showScore, setShowScore] = useState(true);
  const [showActions, setShowActions] = useState(true);
  const [showCompletionRate, setShowCompletionRate] = useState(true);
  const [showCompletionTime, setShowCompletionTime] = useState(true);
  const [showActionResolution, setShowActionResolution] = useState(true);
  const [showGoodPractices, setShowGoodPractices] = useState(true);

  const isPlatformRole = session?.user?.role === 'Developer' || session?.user?.role === 'PlatformManager';
  const isOrgManager = session?.user?.role === 'OrgManager' || session?.user?.role === 'OrgAdmin';
  const isHomeManager = session?.user?.role === 'HomeManager' || session?.user?.role === 'HomeAdmin';

  // CONFIDENTIALITY: Platform roles should NOT access audit reports
  if (!isLoading && isPlatformRole) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600">
              Platform roles do not have access to confidential audit reports. Reports contain sensitive care home data that is restricted to organizational and home-level users only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fetch template types
  const { data: templateTypes = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/template-types"],
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch template categories
  const { data: templateCategories = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/template-categories"],
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch templates
  const { data: templates = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/templates"],
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch organizations (for platform roles)
  const { data: organizations = [] } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["/api/organizations"],
    enabled: !isLoading && isPlatformRole,
  });

  // Fetch summary data
  const { data: summary, isLoading: summaryLoading } = useQuery<ReportSummary>({
    queryKey: ["/api/reports/summary", dateFrom, dateTo, selectedHomes, selectedOrg],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (selectedHomes.length > 0) params.set("homeIds", selectedHomes.join(','));
  if (selectedScoreType && selectedScoreType !== 'all') params.set('scoreType', selectedScoreType);
      if (isPlatformRole && selectedOrg && selectedOrg !== "all") params.set("orgId", selectedOrg);
      const url = `/api/reports/summary${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch summary');
      return response.json();
    },
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch audit list
  const { data: audits = [], isLoading: auditsLoading } = useQuery<AuditReport[]>({
    queryKey: ["/api/reports/audits", dateFrom, dateTo, selectedHomes, selectedStatus, selectedType, selectedCategory, selectedTemplate, selectedOrg],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (selectedHomes.length > 0) params.set("homeIds", selectedHomes.join(','));
      if (selectedStatus && selectedStatus !== "all") params.set("status", selectedStatus);
      if (selectedType && selectedType !== "all") params.set("typeId", selectedType);
      if (selectedCategory && selectedCategory !== "all") params.set("categoryId", selectedCategory);
      if (selectedTemplate && selectedTemplate !== "all") params.set("templateId", selectedTemplate);
      if (isPlatformRole && selectedOrg && selectedOrg !== "all") params.set("orgId", selectedOrg);
      const url = `/api/reports/audits${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch audits');
      return response.json();
    },
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch trends data
  const { data: trends = [], isLoading: trendsLoading } = useQuery<TrendData[]>({
    queryKey: ["/api/reports/trends", dateFrom, dateTo, selectedHomes, selectedOrg],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (selectedHomes.length > 0) params.set("homeIds", selectedHomes.join(','));
      if (isPlatformRole && selectedOrg && selectedOrg !== "all") params.set("orgId", selectedOrg);
      const url = `/api/reports/trends${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch trends');
      return response.json();
    },
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Fetch detailed audit metrics
  const { data: auditMetrics = [], isLoading: metricsLoading } = useQuery<AuditMetric[]>({
    queryKey: ["/api/reports/audit-metrics", dateFrom, dateTo, selectedHomes, selectedStatus, selectedType, selectedCategory, selectedTemplate, selectedOrg],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (selectedHomes.length > 0) params.set("homeIds", selectedHomes.join(','));
      if (selectedStatus && selectedStatus !== "all") params.set("status", selectedStatus);
      if (selectedType && selectedType !== "all") params.set("typeId", selectedType);
      if (selectedCategory && selectedCategory !== "all") params.set("categoryId", selectedCategory);
      if (selectedTemplate && selectedTemplate !== "all") params.set("templateId", selectedTemplate);
      if (isPlatformRole && selectedOrg && selectedOrg !== "all") params.set("orgId", selectedOrg);
      const url = `/api/reports/audit-metrics${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch audit metrics');
      return response.json();
    },
    enabled: !isLoading && !!session?.user && session.user.role !== 'HomeStaff',
  });

  // Wait for session to load
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-primary))] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user has access to reports - HomeStaff cannot access
  if (!session?.user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <CardTitle>Session Error</CardTitle>
            </div>
            <CardDescription>
              Unable to load your session. Please try logging in again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => window.location.href = '/login'} 
              className="w-full"
              data-testid="button-go-to-login"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (session.user.role === 'HomeStaff') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <CardTitle>Access Restricted</CardTitle>
            </div>
            <CardDescription>
              Reports are only available to managers and administrators. Please contact your manager if you need access to reporting features.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setLocation('/dashboard')} 
              className="w-full"
              data-testid="button-back-to-dashboard"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter homes based on selected organization (for platform roles)
  const availableHomes = (isPlatformRole && selectedOrg && selectedOrg !== "all"
    ? summary?.homes.filter(h => {
        // We need to get the home's organization_id from the homes API
        // For now, we'll filter after we fetch all homes
        return true; // Will be filtered by backend based on orgId
      })
    : summary?.homes) || [];

  // Prepare pie chart data
  const pieData = [
  { name: 'Completed', value: summary?.completedAudits || 0, color: 'hsl(var(--chart-2))' },
  { name: 'Pending', value: summary?.pendingAudits || 0, color: 'hsl(var(--chart-4))' },
  ];

  // Prepare bar chart data by type
  const typeScores = audits.reduce((acc: any, audit) => {
    if (audit.score !== null && audit.type_id) {
      const typeName = templateTypes.find((t: any) => t.id === audit.type_id)?.name || 'Unknown';
      if (!acc[typeName]) {
        acc[typeName] = { sum: 0, count: 0 };
      }
      acc[typeName].sum += audit.score;
      acc[typeName].count += 1;
    }
    return acc;
  }, {});

  const barData = Object.keys(typeScores).map(type => ({
    name: type,
    avgScore: Math.round(typeScores[type].sum / typeScores[type].count)
  }));

  const handleExportPDF = async () => {
    try {
      toast({
        title: "Generating PDF...",
        description: "Please wait while we prepare your report.",
      });

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Title
      doc.setFontSize(20);
      doc.text("Smart Care Audits Compliance Report", pageWidth / 2, yPosition, { align: "center" });
      yPosition += 10;

      // Date range
      doc.setFontSize(10);
      const dateRangeText = dateFrom && dateTo 
        ? `${format(new Date(dateFrom), "dd/MM/yyyy")} - ${format(new Date(dateTo), "dd/MM/yyyy")}`
        : "All Dates";
      doc.text(`Report Period: ${dateRangeText}`, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 15;

      // Score type basis
      const scoreTypeText = selectedScoreType === 'scored' ? 'Scored audits only' : selectedScoreType === 'unscored' ? 'Unscored audits only' : 'All audits';
      doc.setFontSize(10);
      doc.text(`Score Basis: ${scoreTypeText}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Export charts if needed
      if (exportContent === 'charts' || exportContent === 'both') {
        doc.setFontSize(14);
        doc.text("Summary Statistics", 20, yPosition);
        yPosition += 10;

        // Export chart images (PieChart and BarChart side-by-side, then LineChart below)
        try {
          // Capture Pie Chart and Bar Chart side-by-side
          const pieChartElement = document.getElementById("report-pie-chart");
          const barChartElement = document.getElementById("report-bar-chart");
          
          if (pieChartElement && barChartElement) {
            // Capture Pie Chart (left side)
            const pieCanvas = await html2canvas(pieChartElement, {
              backgroundColor: '#ffffff',
              scale: 2,
              logging: false,
              useCORS: true,
            });
            const pieImgData = pieCanvas.toDataURL("image/png");
            
            // Capture Bar Chart (right side)
            const barCanvas = await html2canvas(barChartElement, {
              backgroundColor: '#ffffff',
              scale: 2,
              logging: false,
              useCORS: true,
            });
            const barImgData = barCanvas.toDataURL("image/png");
            
            // Place both charts side-by-side (similar to the page layout)
            const chartWidth = 85; // Width for each chart
            const chartHeight = 60; // Height for charts
            const chartSpacing = 5; // Space between charts
            
            // Pie chart on the left
            doc.addImage(pieImgData, "PNG", 15, yPosition, chartWidth, chartHeight);
            // Bar chart on the right
            doc.addImage(barImgData, "PNG", 15 + chartWidth + chartSpacing, yPosition, chartWidth, chartHeight);
            yPosition += chartHeight + 10;
          }
          
          // Capture Line Chart (full width below)
          const lineChartElement = document.getElementById("report-line-chart");
          if (lineChartElement && trends.length > 0) {
            const lineCanvas = await html2canvas(lineChartElement, {
              backgroundColor: '#ffffff',
              scale: 2,
              logging: false,
              useCORS: true,
            });
            const lineImgData = lineCanvas.toDataURL("image/png");
            
            // Add line chart full width
            doc.addImage(lineImgData, "PNG", 15, yPosition, 180, 60);
            yPosition += 70;
          }
        } catch (chartError) {
          console.error('Error capturing charts:', chartError);
          // Add a note that charts couldn't be captured
          doc.setFontSize(10);
          doc.setTextColor(200, 0, 0);
          doc.text("Note: Charts could not be captured. Showing data summary below.", 20, yPosition);
          doc.setTextColor(0, 0, 0);
          yPosition += 10;
        }

        // Add summary statistics text
        doc.setFontSize(10);
        doc.text(`Total Audits: ${summary?.totalAudits || 0}`, 20, yPosition);
        yPosition += 6;
        doc.text(`Completed Audits: ${summary?.completedAudits || 0}`, 20, yPosition);
        yPosition += 6;
        doc.text(`Pending Audits: ${summary?.pendingAudits || 0}`, 20, yPosition);
        yPosition += 6;
        doc.text(`Average Score: ${summary?.averageScore || 0}%`, 20, yPosition);
        yPosition += 6;
        doc.text(`Completion Rate: ${summary?.completionRate || 0}%`, 20, yPosition);
        yPosition += 15;
      }

      // Export table if needed
      if (exportContent === 'table' || exportContent === 'both') {
        // Audits Table
        if (audits.length > 0) {
          doc.setFontSize(14);
          doc.text("Audit Details", 20, yPosition);
          yPosition += 10;

          doc.setFontSize(8);
          // Table headers
          doc.text("Name", 20, yPosition);
          doc.text("Home", 80, yPosition);
          doc.text("Status", 130, yPosition);
          doc.text("Score", 160, yPosition);
          yPosition += 5;

          // Table rows (limit to first 30 to avoid page overflow)
          audits.slice(0, 30).forEach((audit) => {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 20;
            }
            doc.text(audit.name.substring(0, 25), 20, yPosition);
            doc.text(audit.home_name.substring(0, 20), 80, yPosition);
            doc.text(audit.status, 130, yPosition);
            doc.text(audit.score ? `${audit.score}%` : "N/A", 160, yPosition);
            yPosition += 6;
          });

          if (audits.length > 30) {
            yPosition += 5;
            doc.text(`... and ${audits.length - 30} more audits`, 20, yPosition);
          }
        } else {
          doc.setFontSize(10);
          doc.text("No audits found matching the selected filters.", 20, yPosition);
        }
      }

      // Footer
      const timestamp = format(new Date(), "dd/MM/yyyy HH:mm");
      doc.setFontSize(8);
      doc.text(`Generated on ${timestamp}`, 20, doc.internal.pageSize.getHeight() - 10);

      // Download
      const filename = `smartcare-report-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);

      toast({
        title: "Export Successful",
        description: `Report saved as ${filename}`,
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleExportCSV = () => {
    try {
      if (audits.length === 0) {
        toast({
          title: "No Data",
          description: "No audits found to export. Please adjust your filters.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Generating CSV...",
        description: "Please wait while we prepare your report.",
      });

  // Create CSV headers
      const headers = ['Audit Name', 'Home', 'Template', 'Status', 'Score', 'Created Date', 'Created By'];
      
      // Create CSV rows
      const rows = audits.map(audit => [
        audit.name,
        audit.home_name,
        audit.template_name,
        audit.status,
        audit.score !== null ? `${audit.score}%` : 'N/A',
        format(new Date(audit.created_at), 'dd/MM/yyyy'),
        audit.created_by_name
      ]);

      // Combine headers and rows, include filter basis at top
      const scoreBasisLine = `"Score Basis: ${selectedScoreType === 'scored' ? 'Scored' : selectedScoreType === 'unscored' ? 'Unscored' : 'All'}"`;
      const csvContent = [
        scoreBasisLine,
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const filename = `smartcare-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast({
        title: "Export Successful",
        description: `Report saved as ${filename}`,
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to generate CSV. Please try again.",
        variant: "destructive",
      });
    }
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSelectedHomes([]);
    setSelectedStatus("all");
    setSelectedType("all");
    setSelectedCategory("all");
    setSelectedTemplate("all");
    setSelectedOrg("all");
  };

  const toggleHome = (homeId: string) => {
    setSelectedHomes(prev => 
      prev.includes(homeId) 
        ? prev.filter(id => id !== homeId)
        : [...prev, homeId]
    );
  };

  const toggleOrg = (orgId: string) => {
    setSelectedOrg(orgId);
    // Clear home selections when org changes
    setSelectedHomes([]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title font-bold text-slate-900 text-[26px]">Reports & Analytics</h1>
            <p className="text-slate-600 mt-1">
              Comprehensive audit reporting and compliance insights
            </p>
          </div>
          <Popover open={exportOpen} onOpenChange={setExportOpen}>
            <PopoverTrigger asChild>
              <Button 
                className="bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))]"
                disabled={audits.length === 0}
                data-testid="button-export"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Report
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-3">Export Options</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Content</Label>
                      <Select value={exportContent} onValueChange={(v: any) => setExportContent(v)}>
                        <SelectTrigger data-testid="select-export-content">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="charts">Charts Only</SelectItem>
                          <SelectItem value="table">Table Only</SelectItem>
                          <SelectItem value="both">Charts & Table</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">Format</Label>
                      <Select value={exportFormat} onValueChange={(v: any) => setExportFormat(v)}>
                        <SelectTrigger data-testid="select-export-format">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pdf">PDF</SelectItem>
                          <SelectItem value="csv">CSV (Table Only)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))]"
                  onClick={() => {
                    if (exportFormat === 'pdf') {
                      handleExportPDF();
                    } else {
                      handleExportCSV();
                    }
                    setExportOpen(false);
                  }}
                  data-testid="button-confirm-export"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export {exportFormat.toUpperCase()}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Filters Card */}
        <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <Filter className="mr-2 h-5 w-5" />
                    Filters
                  </CardTitle>
                  <CardDescription>
                    {(isPlatformRole || isOrgManager) ? 'Filter reports by date range, organization, homes, status, type, and category' : 'Filter reports by date range, status, type, and category'}
                  </CardDescription>
                </div>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-9 p-0" data-testid="button-toggle-filters">
                    {filtersOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle filters</span>
                  </Button>
                </CollapsibleTrigger>
              </div>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
            {isPlatformRole && (
              <div className="mb-4">
                <Label htmlFor="organization">Organization</Label>
                <Popover open={orgSelectOpen} onOpenChange={setOrgSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={orgSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="button-select-organization"
                    >
                      {selectedOrg === "all"
                        ? "All Organizations"
                        : organizations.find(o => o.id.toString() === selectedOrg)?.name}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search organizations..." data-testid="input-search-organizations" />
                      <CommandList>
                        <CommandEmpty>No organizations found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => toggleOrg("all")}
                            data-testid="item-org-all"
                          >
                            <Checkbox
                              checked={selectedOrg === "all"}
                              className="mr-2"
                            />
                            <span>All Organizations</span>
                          </CommandItem>
                          {organizations.map((org) => (
                            <CommandItem
                              key={org.id}
                              value={org.name}
                              onSelect={() => toggleOrg(org.id.toString())}
                              data-testid={`item-org-${org.id}`}
                            >
                              <Checkbox
                                checked={selectedOrg === org.id.toString()}
                                className="mr-2"
                              />
                              <span>{org.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            {(isPlatformRole || isOrgManager) && (
              <div className="mb-4">
                <Label htmlFor="homes">Care Homes</Label>
                <Popover open={homeSelectOpen} onOpenChange={setHomeSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={homeSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="button-select-homes"
                    >
                      {selectedHomes.length === 0
                        ? "Select homes..."
                        : selectedHomes.length === 1
                        ? availableHomes.find(h => h.id.toString() === selectedHomes[0])?.name
                        : `${selectedHomes.length} homes selected`}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search homes..." data-testid="input-search-homes" />
                      <CommandList>
                        <CommandEmpty>No homes found.</CommandEmpty>
                        <CommandGroup>
                          {availableHomes.map((home) => (
                            <CommandItem
                              key={home.id}
                              value={home.name}
                              onSelect={() => toggleHome(home.id.toString())}
                              data-testid={`item-home-${home.id}`}
                            >
                              <Checkbox
                                checked={selectedHomes.includes(home.id.toString())}
                                className="mr-2"
                              />
                              <span>{home.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedHomes.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-2">
                    {selectedHomes.map((homeId) => {
                      const home = availableHomes.find(h => h.id.toString() === homeId);
                      return (
                        <div
                          key={homeId}
                          className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md flex items-center gap-1"
                          data-testid={`badge-home-${homeId}`}
                        >
                          {home?.name}
                          <button
                            onClick={() => toggleHome(homeId)}
                            className="hover:bg-primary/20 rounded-full p-0.5"
                            data-testid={`button-remove-home-${homeId}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dateFrom">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="input-date-from"
                />
              </div>
              <div>
                <Label htmlFor="dateTo">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="input-date-to"
                />
              </div>
              <div>
                <Label htmlFor="type">Audit Type</Label>
                <Popover open={typeSelectOpen} onOpenChange={setTypeSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={typeSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="select-type"
                    >
                      {selectedType === "all"
                        ? "All Types"
                        : templateTypes.find((t: any) => t.id.toString() === selectedType)?.name || "All Types"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search types..." />
                      <CommandList>
                        <CommandEmpty>No types found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedType("all");
                              setTypeSelectOpen(false);
                            }}
                          >
                            All Types
                          </CommandItem>
                          {templateTypes.map((type: any) => (
                            <CommandItem
                              key={type.id}
                              value={type.name}
                              onSelect={() => {
                                setSelectedType(type.id.toString());
                                setTypeSelectOpen(false);
                              }}
                            >
                              {type.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Popover open={categorySelectOpen} onOpenChange={setCategorySelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={categorySelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="select-category"
                    >
                      {selectedCategory === "all"
                        ? "All Categories"
                        : templateCategories.find((c: any) => c.id.toString() === selectedCategory)?.name || "All Categories"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search categories..." />
                      <CommandList>
                        <CommandEmpty>No categories found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedCategory("all");
                              setCategorySelectOpen(false);
                            }}
                          >
                            All Categories
                          </CommandItem>
                          {templateCategories.map((category: any) => (
                            <CommandItem
                              key={category.id}
                              value={category.name}
                              onSelect={() => {
                                setSelectedCategory(category.id.toString());
                                setCategorySelectOpen(false);
                              }}
                            >
                              {category.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="audit">Audit</Label>
                <Popover open={templateSelectOpen} onOpenChange={setTemplateSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={templateSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="select-audit"
                    >
                      {selectedTemplate === "all"
                        ? "All Audits"
                        : templates.find((t: any) => t.id.toString() === selectedTemplate)?.name || "All Audits"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search audits..." />
                      <CommandList>
                        <CommandEmpty>No audits found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedTemplate("all");
                              setTemplateSelectOpen(false);
                            }}
                          >
                            All Audits
                          </CommandItem>
                          {templates.map((template: any) => (
                            <CommandItem
                              key={template.id}
                              value={template.name}
                              onSelect={() => {
                                setSelectedTemplate(template.id.toString());
                                setTemplateSelectOpen(false);
                              }}
                            >
                              {template.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Popover open={statusSelectOpen} onOpenChange={setStatusSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={statusSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="select-status"
                    >
                      {selectedStatus === "all" ? "All Statuses" : selectedStatus}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search status..." />
                      <CommandList>
                        <CommandEmpty>No status found.</CommandEmpty>
                        <CommandGroup>
                          {["all", "Draft", "Submitted", "Approved"].map((status) => (
                            <CommandItem
                              key={status}
                              value={status}
                              onSelect={() => {
                                setSelectedStatus(status);
                                setStatusSelectOpen(false);
                              }}
                            >
                              {status === "all" ? "All Statuses" : status}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="scoreType">Score Type</Label>
                <Popover open={scoreTypeSelectOpen} onOpenChange={setScoreTypeSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={scoreTypeSelectOpen}
                      className="w-full justify-between mt-1"
                      data-testid="select-score-type"
                    >
                      {selectedScoreType === 'all' ? 'All Scores' : selectedScoreType === 'scored' ? 'Scored' : 'Unscored'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Filter by score type..." />
                      <CommandList>
                        <CommandEmpty>No types found.</CommandEmpty>
                        <CommandGroup>
                          {['all','scored','unscored'].map((t) => (
                            <CommandItem
                              key={t}
                              value={t}
                              onSelect={() => {
                                setSelectedScoreType(t as any);
                                setScoreTypeSelectOpen(false);
                              }}
                            >
                              {t === 'all' ? 'All Scores' : t === 'scored' ? 'Scored' : 'Unscored'}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="mt-4">
              <Button 
                variant="outline" 
                onClick={clearFilters}
                data-testid="button-clear-filters"
              >
                Clear All Filters
              </Button>
            </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Summary Cards */}
        {summaryLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-slate-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Audits</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="text-total-audits">
                      {summary?.totalAudits || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Completed</p>
                    <p className="text-3xl font-bold text-green-600" data-testid="text-completed-audits">
                      {summary?.completedAudits || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Pending</p>
                    <p className="text-3xl font-bold text-orange-600" data-testid="text-pending-audits">
                      {summary?.pendingAudits || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Avg Score</p>
                    <p className="text-3xl font-bold text-[hsl(var(--brand-primary))]" data-testid="text-average-score">
                      {summary?.averageScore || 0}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-[hsl(var(--brand-primary))]/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-[hsl(var(--brand-primary))]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Audit Metrics Report */}
        <Collapsible open={metricsOpen} onOpenChange={setMetricsOpen}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="mr-2 h-5 w-5" />
                    Detailed Audit Metrics Report
                  </CardTitle>
                  <CardDescription>
                    Comprehensive audit performance metrics including scores, actions, and completion times
                  </CardDescription>
                </div>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-9 p-0" data-testid="button-toggle-metrics">
                    {metricsOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle metrics</span>
                  </Button>
                </CollapsibleTrigger>
              </div>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                {/* Field Selection */}
                <div className="mb-4 p-4 border rounded-lg bg-slate-50">
                  <Label className="text-sm font-semibold mb-3 block">Select Fields to Display:</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-score"
                        checked={showScore}
                        onCheckedChange={(checked) => setShowScore(checked as boolean)}
                        data-testid="checkbox-show-score"
                      />
                      <label htmlFor="show-score" className="text-sm cursor-pointer">
                        Audit Score
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-actions"
                        checked={showActions}
                        onCheckedChange={(checked) => setShowActions(checked as boolean)}
                        data-testid="checkbox-show-actions"
                      />
                      <label htmlFor="show-actions" className="text-sm cursor-pointer">
                        Actions Triggered
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-completion-rate"
                        checked={showCompletionRate}
                        onCheckedChange={(checked) => setShowCompletionRate(checked as boolean)}
                        data-testid="checkbox-show-completion-rate"
                      />
                      <label htmlFor="show-completion-rate" className="text-sm cursor-pointer">
                        Action Completion Rate
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-completion-time"
                        checked={showCompletionTime}
                        onCheckedChange={(checked) => setShowCompletionTime(checked as boolean)}
                        data-testid="checkbox-show-completion-time"
                      />
                      <label htmlFor="show-completion-time" className="text-sm cursor-pointer">
                        Audit Completion Time
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-action-resolution"
                        checked={showActionResolution}
                        onCheckedChange={(checked) => setShowActionResolution(checked as boolean)}
                        data-testid="checkbox-show-action-resolution"
                      />
                      <label htmlFor="show-action-resolution" className="text-sm cursor-pointer">
                        Action Resolution Time
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="show-good-practices"
                        checked={showGoodPractices}
                        onCheckedChange={(checked) => setShowGoodPractices(checked as boolean)}
                        data-testid="checkbox-show-good-practices"
                      />
                      <label htmlFor="show-good-practices" className="text-sm cursor-pointer">
                        Good Practices
                      </label>
                    </div>
                  </div>
                </div>

                {/* Metrics Table */}
                {metricsLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-slate-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : auditMetrics.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-slate-400" />
                    <p className="mt-4 text-lg font-medium text-slate-900">No submitted audits found</p>
                    <p className="text-slate-600">
                      Metrics are only available for submitted audits
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-slate-900">Audit Name</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-900">Home</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-900">Template</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-900">Submitted</th>
                          {showScore && <th className="text-left py-3 px-4 font-medium text-slate-900">Score</th>}
                          {showActions && <th className="text-left py-3 px-4 font-medium text-slate-900">Actions</th>}
                          {showCompletionRate && <th className="text-left py-3 px-4 font-medium text-slate-900">Action Rate</th>}
                          {showCompletionTime && <th className="text-left py-3 px-4 font-medium text-slate-900">Completion Time</th>}
                          {showActionResolution && <th className="text-left py-3 px-4 font-medium text-slate-900">Avg Resolution</th>}
                          {showGoodPractices && <th className="text-left py-3 px-4 font-medium text-slate-900">Good Practices</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {auditMetrics.map((metric) => (
                          <tr key={metric.id} className="border-b hover:bg-slate-50" data-testid={`row-metric-${metric.id}`}>
                            <td className="py-3 px-4 font-medium text-slate-900">{metric.name}</td>
                            <td className="py-3 px-4 text-slate-600">{metric.home_name}</td>
                            <td className="py-3 px-4 text-slate-600">{metric.template_name}</td>
                            <td className="py-3 px-4 text-slate-600">
                              {metric.submitted_at ? format(new Date(metric.submitted_at), "dd/MM/yyyy") : 'N/A'}
                            </td>
                            {showScore && (
                              <td className="py-3 px-4 font-semibold text-[hsl(var(--brand-primary))]">
                                {metric.score}%
                              </td>
                            )}
                            {showActions && (
                              <td className="py-3 px-4 text-slate-600">
                                {metric.completed_actions}/{metric.total_actions}
                              </td>
                            )}
                            {showCompletionRate && (
                              <td className="py-3 px-4">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  metric.action_completion_rate === 100
                                    ? 'bg-green-100 text-green-800'
                                    : metric.action_completion_rate >= 50
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-orange-100 text-orange-800'
                                }`}>
                                  {metric.action_completion_rate}%
                                </span>
                              </td>
                            )}
                            {showCompletionTime && (
                              <td className="py-3 px-4 text-slate-600">
                                {metric.completion_time_hours !== null 
                                  ? `${metric.completion_time_hours}h` 
                                  : 'N/A'}
                              </td>
                            )}
                            {showActionResolution && (
                              <td className="py-3 px-4 text-slate-600">
                                {metric.avg_action_resolution_hours !== null 
                                  ? `${metric.avg_action_resolution_hours}h` 
                                  : 'N/A'}
                              </td>
                            )}
                            {showGoodPractices && (
                              <td className="py-3 px-4 text-slate-600">
                                {metric.good_practices_count || 0}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Visual Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart - Completion Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PieChartIcon className="mr-2 h-5 w-5" />
                Audit Completion Status
              </CardTitle>
              <CardDescription>Click a segment to filter audits by status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[300px]" id="report-pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => {
                        if (data && data.name) {
                          setSelectedStatus(data.name);
                          setFiltersOpen(true);
                        }
                      }}
                      cursor="pointer"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      onClick={(data) => {
                        if (data && data.value) {
                          setSelectedStatus(data.value);
                          setFiltersOpen(true);
                        }
                      }}
                      wrapperStyle={{ cursor: 'pointer' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart - Average Scores by Type */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="mr-2 h-5 w-5" />
                Average Scores by Type
              </CardTitle>
              <CardDescription>Click a bar to filter audits by type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[300px]" id="report-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="avgScore" 
                      fill="hsl(var(--brand-primary))" 
                      name="Avg Score (%)"
                      onClick={(data) => {
                        if (data && data.typeId) {
                          setSelectedType(data.typeId.toString());
                          setFiltersOpen(true);
                        }
                      }}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Line Chart - Compliance Trends */}
        {trends.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                Compliance Trends Over Time
              </CardTitle>
              <CardDescription>
                Weekly audit completion and performance trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[300px]" id="report-line-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="week" 
                    tickFormatter={(value) => format(new Date(value), 'dd MMM')}
                  />
                  <YAxis yAxisId="left" domain={[0, 'auto']} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                  <Tooltip 
                    labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="completed" 
                    stroke="hsl(var(--chart-2))" 
                    name="Completed Audits" 
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="avgScore" 
                    stroke="hsl(var(--brand-primary))" 
                    name="Avg Score (%)" 
                  />
                </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Details Table */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Details</CardTitle>
            <CardDescription>
              Detailed list of all audits matching your filters
            </CardDescription>
          </CardHeader>
          <CardContent>
            {auditsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-slate-200 rounded animate-pulse"></div>
                ))}
              </div>
            ) : audits.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-slate-400" />
                <p className="mt-4 text-lg font-medium text-slate-900">No audits found</p>
                <p className="text-slate-600">
                  Try adjusting your filters to see more results
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Audit Name</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Home</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Template</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Score</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Created</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-900">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audits.map((audit) => (
                      <tr key={audit.id} className="border-b hover:bg-slate-50" data-testid={`row-audit-${audit.id}`}>
                        <td className="py-3 px-4 font-medium text-slate-900">{audit.name}</td>
                        <td className="py-3 px-4 text-slate-600">{audit.home_name}</td>
                        <td className="py-3 px-4 text-slate-600">{audit.template_name}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              audit.status === 'Approved'
                                ? 'bg-green-100 text-green-800'
                                : audit.status === 'Submitted'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            {audit.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold text-[hsl(var(--brand-primary))]">
                          {audit.score !== null ? `${audit.score}%` : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-slate-600">
                          {format(new Date(audit.created_at), "dd/MM/yyyy")}
                        </td>
                        <td className="py-3 px-4 text-slate-600">{audit.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
